import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { ChevronDownIcon } from "../icons";
import { useSidebar } from "../context/SidebarContext";
import authService, { AccessibleCompany } from "../services/authService";

const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return (words[0][0] ?? "?").toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

interface CompanyAvatarProps {
    company: AccessibleCompany;
    size?: "sm" | "md";
}

function CompanyAvatar({ company, size = "md" }: CompanyAvatarProps) {
    const [errored, setErrored] = useState(false);
    const dimensions = size === "md" ? "w-9 h-9 text-sm" : "w-8 h-8 text-xs";

    if (company.logoUrl && !errored) {
        return (
            <img
                src={company.logoUrl}
                alt={company.fantasyName}
                onError={() => setErrored(true)}
                className={`${dimensions} rounded-full object-cover shrink-0`}
            />
        );
    }

    return (
        <div
            className={`flex items-center justify-center ${dimensions} bg-blue-100 text-blue-700 rounded-full dark:bg-blue-900/30 dark:text-blue-400 font-semibold shrink-0`}
        >
            {getInitials(company.fantasyName)}
        </div>
    );
}

export default function CompanySelector() {
    const { isExpanded, isHovered, isMobileOpen } = useSidebar();
    const showFull = isExpanded || isHovered || isMobileOpen;

    const [open, setOpen] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [companies, setCompanies] = useState<AccessibleCompany[]>(() =>
        authService.getCompanies()
    );
    const [active, setActive] = useState<AccessibleCompany | null>(() =>
        authService.getActiveCompany()
    );
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Refresh defensivo da lista ao montar
    useEffect(() => {
        let cancelled = false;
        authService
            .fetchMyCompanies()
            .then(({ activeCompanyId, companies: fresh }) => {
                if (cancelled) return;
                setCompanies(fresh);
                const found =
                    fresh.find((c) => c.id === activeCompanyId) ?? null;
                if (found) setActive(found);
            })
            .catch(() => {
                /* mantém o cache local em caso de falha */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = async (company: AccessibleCompany) => {
        if (switching) return;
        setOpen(false);
        if (company.id === active?.id) return;

        try {
            setSwitching(true);
            await authService.switchCompany(company.id);
            window.location.reload();
        } catch (error) {
            console.error("Erro ao trocar de empresa:", error);
            setSwitching(false);
        }
    };

    if (!active || companies.length === 0) return null;

    const isStatic = companies.length === 1;

    return (
        <div
            ref={wrapperRef}
            className="relative border-t border-gray-200 dark:border-gray-800 -mx-5 px-3 py-3"
        >
            <button
                type="button"
                disabled={isStatic || switching}
                onClick={() => !isStatic && showFull && setOpen((v) => !v)}
                className={`flex items-center w-full rounded-lg p-2 transition-colors ${
                    isStatic
                        ? "cursor-default"
                        : "hover:bg-gray-100 dark:hover:bg-white/[0.05]"
                } ${showFull ? "justify-start gap-3" : "justify-center"} ${
                    switching ? "opacity-60 cursor-wait" : ""
                }`}
            >
                <CompanyAvatar company={active} />
                {showFull && (
                    <>
                        <span className="flex flex-col flex-1 min-w-0 text-left">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {active.fantasyName}
                            </span>
                            {active.isCollaborator && (
                                <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Colaborador
                                </span>
                            )}
                        </span>
                        {!isStatic && (
                            <ChevronDownIcon
                                className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${
                                    open ? "rotate-180" : ""
                                }`}
                            />
                        )}
                    </>
                )}
            </button>

            {open && showFull && !isStatic && (
                <div className="absolute left-3 right-3 bottom-full mb-2 rounded-xl border border-gray-200 bg-white shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark z-50 max-h-72 overflow-y-auto">
                    <ul className="py-2">
                        {companies.map((company) => {
                            const isActive = company.id === active.id;
                            return (
                                <li key={company.id}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelect(company)}
                                        className={`flex items-center w-full gap-3 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-white/[0.05] transition-colors ${
                                            isActive
                                                ? "bg-gray-50 dark:bg-white/[0.03]"
                                                : ""
                                        }`}
                                    >
                                        <CompanyAvatar
                                            company={company}
                                            size="sm"
                                        />
                                        <span className="flex flex-col flex-1 min-w-0">
                                            <span className="text-sm text-gray-900 dark:text-white truncate">
                                                {company.fantasyName}
                                            </span>
                                            {company.isCollaborator && (
                                                <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    Colaborador
                                                </span>
                                            )}
                                        </span>
                                        {isActive && (
                                            <Check className="w-4 h-4 text-brand-500 shrink-0" />
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
